/**
 * SYM-6288 (20 Sep 2026). The wizard pop-up must fit inside the window.
 *
 * What went wrong: the pop-up's height followed its content, and the tallest
 * thing in it is the 9:16 story preview (384px wide makes it 683px tall, before
 * the play button and the two rows of chrome). On a laptop that is more than the
 * window, so either the steps row or the button row fell off screen and people
 * concluded they could not move on.
 *
 * The rule now: the pop-up has a height of its own, taken from the window, and
 * everything inside it is sized from THAT, never the other way round. The two
 * values below are the whole arithmetic; the rest is flex boxes in
 * VideoWizard.tsx and VideoPreviewModal.tsx.
 */

/**
 * How tall the pop-up is.
 *
 * `100dvh - 2rem`: the shared Modal keeps 1rem of air above and below the panel
 * (its container is `p-4`), so this is exactly the room there is.
 *
 * `940px`: on a tall monitor the pop-up stops growing here. It is the height at
 * which a 9:16 preview reaches its full 384px width with the play button and
 * the "older version" note under it; more than that is only empty space.
 *
 * Hypothesis, unverified: a browser too old to know `dvh` (Chrome before 108)
 * drops this line and falls back to the shared Modal's own 80vh cap, which
 * still fits the window. Not tested in such a browser.
 */
export const WIZARD_PANEL_HEIGHT = 'min(calc(100dvh - 2rem), 940px)';

/** The preview never grows past this, as before (it was Tailwind's max-w-sm). */
export const PREVIEW_MAX_WIDTH = '24rem';

/** The same number, in pixels, for the arithmetic below. 24rem at the root 16px. */
const PREVIEW_MAX_WIDTH_PX = 384;

/** The padding the preview pane keeps on each side of the video (Tailwind p-6). */
const PREVIEW_PANE_PADDING_PX = 24;

/**
 * What the Preview step gives the video, as a share of the pop-up. The slide
 * list and its "where to change this" message take the rest.
 *
 * SYM-6308. It was 60/40 in the video's favour on every step. The list now
 * carries the message as well, so it takes a little more.
 */
export const PREVIEW_PANE_SHARE = 0.55;

/** The width of the pane the slide list sits in, as CSS. */
export const REVIEW_LIST_PANE_WIDTH = `${(1 - PREVIEW_PANE_SHARE) * 100}%`;

/**
 * How wide the pop-up is allowed to get.
 *
 * SYM-6308 (22 Sep 2026). This was 1200px, and it was that wide because a
 * playing video sat beside the working pane on every step. The video is now
 * only on the Preview step, so the width is picked from that step, which is the
 * widest thing the pop-up still has to hold:
 *
 *   the video never grows past 384px (PREVIEW_MAX_WIDTH), its pane keeps 24px
 *   of padding on each side, and that pane is 55% of the pop-up. So the pop-up
 *   has to be (384 + 48) / 0.55 = 785px before the video starts being squeezed.
 *
 * 880px is that, rounded up: the video keeps its full width with room to spare,
 * and the slide list gets 396px for a number, a thumbnail, a headline and a
 * length.
 *
 * Measured against what 1200px actually gave each pane, rather than against the
 * number: on Scenes the three panes asked for 160% of the pop-up and all three
 * were shrunk to fit, so the slide editor really had 487px. At 880px it gets
 * the 65% it asks for, 572px. The pop-up is a third narrower and the step where
 * the work happens is wider than it was.
 */
export const WIZARD_PANEL_MAX_WIDTH = '880px';

/**
 * The narrowest pop-up at which the video is still drawn at its full width.
 * Exported so the width above cannot quietly drift under its own reason.
 */
export const widthTheVideoNeeds = (
  share: number = PREVIEW_PANE_SHARE
): number => (PREVIEW_MAX_WIDTH_PX + 2 * PREVIEW_PANE_PADDING_PX) / share;

/** The shape of the pane when no plan says otherwise: 9:16, story and reels. */
const STORY_SHAPE = 9 / 16;

/**
 * The shape of the preview box, as width / height.
 *
 * Unchanged behaviour, only moved here so it can be tested: a plan states its
 * own canvas and wins; without one the caller's label decides, and the wizard
 * has always passed '1:1' there.
 */
export const previewShape = (
  ratio: number | undefined,
  aspectRatio: string
): number => {
  if (ratio && Number.isFinite(ratio) && ratio > 0) return ratio;

  return aspectRatio === '1:1' ? 1 : STORY_SHAPE;
};

/**
 * How wide the preview box is, as CSS. Its height follows from `aspect-ratio`.
 *
 * The box sits in a slot that takes whatever height is left in the pane. `cqw`
 * and `cqh` are that slot's width and height, so the box is the largest one of
 * the right shape that fits in BOTH directions: as wide as the slot, or as wide
 * as the slot's height allows, whichever is smaller. That is why the video may
 * get smaller on a short window but is never cropped or stretched.
 *
 * The width is set and the height derived, not the reverse, because a box with
 * both set ignores `aspect-ratio`, and Track1PreviewFrame places everything in
 * percentages of this box: a box of the wrong shape would distort the frame.
 */
export const previewBoxWidth = (shape: number): string =>
  `min(100cqw, ${PREVIEW_MAX_WIDTH}, calc(100cqh * ${shape}))`;
